/*
 * @format
 * @flow
 */
import React, { useState } from 'react';
import {
  SingleDatePicker,
  Icons,
  RequiredValidation,
  TYPES,
  Label,
  Button,
  FlexRow,
  FlexCol,
  TextFieldStateless,
  Heading,
  NumberBullet,
  NumberField,
  Text,
  Stack
} from 'sarsaparilla';
import { get, keys } from 'lodash';
import moment from 'moment';

import {
  isQuotaModifyAllowed as inyoIsQuotaModifyAllowed,
  campsiteSelected,
  MT_WHITNEY_DAY_USE_DIVISION_ID,
  MT_WHITNEY_PERMIT_ID,
} from 'ui-inyonwhitney/src/utils/inyoUtils';
import {
  MONTH_DAY_YEAR_FORMAT,
  MONTH_DAY_YEAR_NUM_FORMAT,
  AVAILABILITY_KEY_MAP_FORMAT,
  IS_CAMPSITE_PREFERENCES_EMPTY,
  IS_FIRST_CAMPSITE_INPUT_EMPTY,
  IS_EXIT_DATE_EMPTY,
  STAY_LIMIT_RULE_NAME,
  SIERRA_NF_ID,
  SEKI_ID,
  PARIA_CANYON_ID,
  HUMBOLDT_TOIYABE_ID,
  INYO_NF_ID,
  YOSE_NP_ID,
} from 'ui-permit/src/constants';
import { readRuleValue } from 'ui-permit/src/utils/utils';
import { PERMIT as permitProps } from 'ui-permit/src/props/permit';
import { isQuotaModifyAllowed as sharedPermitIsQuotaModifyAllowed } from 'ui-permit/shared-components/utils/issuance-utils'

import type { DataBindingType, GenericReservationType, ValidationType } from '../types';
import {
  ATTRIBUTE_DATE_DATA_TYPE_PROP_PATH,
  CAMPSITE_PREFERENCE_PROP_PATH,
  GROUP_INFO_PROP_PATH,
  SELECTED_GROUP_SIZE_PROP_PATH,
  GROUP_DETAIL_ALT_PROP_PATH,
  GROUP_LEADERS_GUIDES_PROP_PATH,
  GROUP_SIZE_PROP_PATH,
} from '../constants/propPaths';
import CampsiteSelectionList from './CampsiteSelectionList';
import ExitPoint from './ExitPoint';

const isQuotaModifyAllowed = (reservation, permit) => {
    // Land Permits and Mt Whitney are using ui-inyonwhitney logic (inyoIsQuotaModifyAllowed)
    // any new permits will use a new unified logic and will leave a
    // TODO to refactor logic on this past permits into new one (adding missing rules, for example if needed)
    if([SIERRA_NF_ID, SEKI_ID, PARIA_CANYON_ID, HUMBOLDT_TOIYABE_ID, INYO_NF_ID, MT_WHITNEY_PERMIT_ID, YOSE_NP_ID].includes(permit.id)) {
        return inyoIsQuotaModifyAllowed(reservation, permit)
    }
    return sharedPermitIsQuotaModifyAllowed(reservation, permit)
}

const defaultDataBindingMap = {
  [CAMPSITE_PREFERENCE_PROP_PATH]: 'campsite_preferences',
  [GROUP_INFO_PROP_PATH]: 'group_info',
  [SELECTED_GROUP_SIZE_PROP_PATH]: 'selected_group_size',
  [GROUP_DETAIL_ALT_PROP_PATH]: 'alt_group_leaders',
  [GROUP_LEADERS_GUIDES_PROP_PATH]: 'guides',
  [GROUP_SIZE_PROP_PATH]: 'group_size',
};

const defaultAttributes = {
  [ATTRIBUTE_DATE_DATA_TYPE_PROP_PATH]: 'moment',
};

type AttributesType = {
  [attributeName: string]: string
}

type PropTypes = {
  addFieldRef: Function,
  displayValidationError: boolean,
  divisionCampsites: Array<Object>,
  entrancesExits: Object,
  history: TYPES.HISTORY,
  isInternal: boolean,
  maxGroupSize: number,
  modifyQuotaButtonText?: string,
  name: string,
  onModifyQuota?: Function,
  permit: permitProps,
  permitCampsites: Array<Object>,
  pActions: {
    formValidationChange: Function,
    updatePendingReservation: Function,
    redirectToAvailabilityPage: Function,
    setStayOvernightCampsite: Function,
    updateCalculatedLineItems: Function,
  },
  renderSuggestion: Function,
  reservation: GenericReservationType,
  useEntrancesToPopulateSelect: boolean,
  validation: ValidationType,
  campsiteLabel?: string,
};

const isDayOutsideStayLimit = (day, reservation, permit) => {
  const entryDate = moment.utc(reservation.start_date).add(1, 'day');
  const exitDate = day.clone();
  const isBeforeEntry = exitDate.isBefore(entryDate);
  let isAfterStayLimit = false;
  const stayLimit = Number(readRuleValue(STAY_LIMIT_RULE_NAME, permit.rules, reservation.division_id));
  if (stayLimit) {
    const maxDate = entryDate.add(stayLimit, 's');
    isAfterStayLimit = exitDate.isAfter(maxDate);
  }
  return isBeforeEntry || isAfterStayLimit;
}

export default function TripItineraryHOC(
  _: any,
  dataBindingsMap: DataBindingType = defaultDataBindingMap,
  attributes: AttributesType = defaultAttributes
) {

  const defaultProps = {
    divisionCampsites: [],
  };

  function TripItinerary(props: PropTypes) {
    const [dateFocused, setDateFocused] = useState(null);
    const {
      name: componentName,
      reservation,
      permit,
      divisionCampsites,
      permitCampsites,
      validation,
      displayValidationError,
      history,
      pActions,
      isInternal,
      useEntrancesToPopulateSelect,
      entrancesExits,
      addFieldRef,
      modifyQuotaButtonText = "Increase Group Size in Detailed Availability",
      onModifyQuota,
      campsiteLabel = "Campsite Locations",
    } = props;

    const onFormValidate = (isInValid, error, path) => {
      if (isInValid) {
        pActions.formValidationChange(`${reservation.id}_${path}`, error);
      } else {
        pActions.formValidationChange(`${reservation.id}_${path}`, null);
      }
    };

    const onPermitDateChange = params => {
      if (!params) return;

      const { id } = reservation;
      let selectedDate = moment.utc(params.format(AVAILABILITY_KEY_MAP_FORMAT)).toJSON();
      if (attributes[ATTRIBUTE_DATE_DATA_TYPE_PROP_PATH] === 'text') {
        selectedDate = params.format(AVAILABILITY_KEY_MAP_FORMAT);
      }

      const preferences = {
        ...get(reservation, dataBindingsMap[CAMPSITE_PREFERENCE_PROP_PATH]),
      };
      const newPreferences = {};
      Object.keys(preferences).forEach(p => {
        if (moment.utc(p) - moment.utc(selectedDate) < 0) {
          newPreferences[p] = preferences[p];
        }
      });

      pActions.updatePendingReservation(id, 'end_date', selectedDate);
      pActions.updatePendingReservation(
        id,
        dataBindingsMap[CAMPSITE_PREFERENCE_PROP_PATH],
        newPreferences
      );
    };

    const onChangeDetailsClick = () => {
      // is field sales, pass existing issuance for modification
      props.pActions.redirectToAvailabilityPage(reservation, history);
    };

    const handleModifyQuotaClicked = () => {
      if(onModifyQuota) {
        onModifyQuota(reservation);
      }
      else if(pActions.redirectToAvailabilityPage) {
        pActions.redirectToAvailabilityPage(reservation, history);
      }
    }

    const isOutsideRange = day => {
      return isDayOutsideStayLimit(day, reservation, permit);
    };

    const stayNightCampsiteSelected = (date, name) => {
      const preferences = {
        ...get(reservation, dataBindingsMap[CAMPSITE_PREFERENCE_PROP_PATH]),
      };
      const convertedPreferences = {};
      keys(preferences).forEach(kkey => {
        const newKey = moment.utc(kkey).toJSON();
        convertedPreferences[newKey] = preferences[kkey];
      });

      if (name === '') {
        delete convertedPreferences[date];
      } else {
        convertedPreferences[date] = name;
      }

      pActions.updatePendingReservation(
        reservation.id,
        dataBindingsMap[CAMPSITE_PREFERENCE_PROP_PATH],
        convertedPreferences
      );
      pActions.setStayOvernightCampsite(date, name);
    };

    const renderExitDateOption = () => {
      const startDate = moment.utc(reservation.start_date);
      const endDate = moment.utc(reservation.end_date);

      if (reservation.division_id === MT_WHITNEY_DAY_USE_DIVISION_ID) {
        return (
          <p className="grayed">{endDate ? endDate.format(MONTH_DAY_YEAR_FORMAT) : ''}</p>
        );
      }

      if (
        dateFocused === null &&
        isInternal &&
        endDate.diff(startDate, 'days') + 1 <= 1
      ) {
        endDate.add(1, 'day');
        onPermitDateChange(endDate);
      }
      const date =
        dateFocused !== null || isInternal || endDate.isAfter(startDate) ? endDate : null;
      return (
        <>
          <SingleDatePicker
            ref={addFieldRef?.(componentName, 0)}
            id="exitDateOption"
            ariaLabel="Exit Date"
            date={date}
            isRequired
            numberOfMonths={1}
            initialVisibleMonth={() => endDate}
            focused={dateFocused}
            isOutsideRange={isOutsideRange}
            showDefaultInputIcon
            onDateChange={onPermitDateChange}
            onFocusChange={({ focused }) => setDateFocused(focused)}
          />
          <RequiredValidation
            value={date ? date.format(AVAILABILITY_KEY_MAP_FORMAT) : ''}
            displayInSummary
            onValidated={(isInValid, error) =>
              onFormValidate(isInValid, error, IS_EXIT_DATE_EMPTY)
            }
            controlLabel="Exit Date"
          />
        </>
      );
    };

    const getMaxGroupSize = () => {
      if (props.maxGroupSize) {
        return props.maxGroupSize;
      }
      return get(reservation, dataBindingsMap[SELECTED_GROUP_SIZE_PROP_PATH], 1);
    };

    const handleMemberCountChange = (value) => {
      const groupMember = get(reservation, `${dataBindingsMap[GROUP_INFO_PROP_PATH]}[0]`, { start_date: reservation.start_date });
      const maxMemberQuota = getMaxGroupSize()
      const altCount = get(reservation, `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}.length`, 0);
      const guidesCount = get(reservation, `${dataBindingsMap[GROUP_LEADERS_GUIDES_PROP_PATH]}.length`, 0);
      const minMemberQuota = altCount + guidesCount + 1;

      if (value >= minMemberQuota && value <= maxMemberQuota) {
        pActions.updatePendingReservation(reservation.id, dataBindingsMap[GROUP_SIZE_PROP_PATH], value);
        let groupInfo = []
        for (let ii = 0; ii < value; ii++) {
          groupInfo = [...groupInfo, groupMember]
        }
        pActions.updatePendingReservation(reservation.id, dataBindingsMap[GROUP_INFO_PROP_PATH], groupInfo);
      }
      if (pActions.updateCalculatedLineItems) {
        pActions.updateCalculatedLineItems();
      }
    }

    const isMtWhitneyDayUse = reservation.division_id === MT_WHITNEY_DAY_USE_DIVISION_ID;
    const startDate = moment.utc(reservation.start_date);
    const endDate = moment.utc(reservation.end_date);
    const selectedGroupSize = get(reservation,`${dataBindingsMap[GROUP_INFO_PROP_PATH]}.length`, 0);

    const selectedCampsites = get(reservation, dataBindingsMap[CAMPSITE_PREFERENCE_PROP_PATH], {}) ?? {};
    const entrance = useEntrancesToPopulateSelect
      ? get(entrancesExits, 'validEntrances', [])
      : get(permit, ['divisions', reservation.division_id], {});

    const campsitePreferences =
      get(reservation, 'campsite_preferences') ||
      get(reservation, 'extra_fields.campsite_preferences') ||
      {};
    const numberOfDays = endDate ? endDate.diff(startDate, 'days') + 1 : 0;
    const isCampsiteSelected = numberOfDays > 1 ? campsiteSelected(campsitePreferences) : '';

    const helpDisplayText1 = isMtWhitneyDayUse ? (
      <Stack>
        <Text>
          You have selected the entry date, entry point, and group size listed below for
          your trip. Next, select your exit date, exit point, and {campsiteLabel.toLowerCase()} to
          complete your itinerary.
        </Text>
        <Heading headingLevel={3}>
          <NumberBullet>{1}</NumberBullet>You have selected the entry point, entry date, and group size listed below
          for you trip:
        </Heading>
      </Stack>
    ) : (
      <Heading headingLevel={3}>
        <NumberBullet>{1}</NumberBullet>You have selected the following trip:
      </Heading>
    );

    const helpDisplayText2 = !isMtWhitneyDayUse
    ? `Next, select your exit point, exit date, and ${campsiteLabel.toLowerCase()} to complete your itinerary:`
    : 'To complete your itinerary information please select the route  you will be using during your trip:';

    const exitLabel = !isMtWhitneyDayUse ? 'Exit Point' : 'Route';

    const maxMemberQuota = getMaxGroupSize();

    const validationError =
      validation?.[`${reservation.id}_${IS_CAMPSITE_PREFERENCES_EMPTY}`];
    const validationHighlightInput =
      validation?.[`${reservation.id}_${IS_FIRST_CAMPSITE_INPUT_EMPTY}`] &&
      displayValidationError
        ? 'rec-error'
        : '';
    const validationHighlight =
      !validationHighlightInput && validationError && displayValidationError
        ? 'permit-validation-highlight'
        : '';

    const renderQuotaModifyButtons = isQuotaModifyAllowed(reservation, permit) && !reservation?.hideQuotaModify;

    return (
      <Stack space="gutter">
        <Heading headingLevel={2} appearance="h5">Trip Itinerary</Heading>
        <FlexRow>
          <FlexCol xs={12}>
            {helpDisplayText1}
          </FlexCol>
          <FlexCol md={6} lg={4} className="mt-1">
            <TextFieldStateless
              label="Entry Point"
              id="entry-point"
              value={entrance.name}
              isDisabled />
            {renderQuotaModifyButtons && (
              <Button
                appearance="link"
                onClick={onChangeDetailsClick}
                iconBeforeElement={<Icons.IconEdit />}>
                Change entry details
              </Button>
            )}
          </FlexCol>
          <FlexCol md={6} lg={4} className="mt-1">
            <TextFieldStateless
              label="Entry Date"
              id="entry-date"
              value={startDate.format(MONTH_DAY_YEAR_NUM_FORMAT)}
              isDisabled
              iconElement={<Icons.IconCalendar />}
            />
          </FlexCol>
          <FlexCol md={6} lg={4} className="mt-1">
            <Label htmlFor="total-group-size">
              Total Group Size
            </Label>
            <NumberField
              ref={addFieldRef?.(componentName, 1)}
              id="total-group-size"
              min={1}
              max={maxMemberQuota}
              value={selectedGroupSize}
              units="guests"
              onChange={handleMemberCountChange}
            />
            {renderQuotaModifyButtons && (
              <Button
                appearance="link"
                onClick={handleModifyQuotaClicked}
                iconBeforeElement={<Icons.IconEdit />}>
                {modifyQuotaButtonText}
              </Button>
            )}
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol xs={12}>
            <Heading headingLevel={3}>
              <NumberBullet>{2}</NumberBullet>
              {helpDisplayText2}
            </Heading>
          </FlexCol>
          <FlexCol md={6} lg={4} className="mt-1">
            <ExitPoint
              inputRef={addFieldRef?.(componentName, 2)}
              exitLabel={exitLabel}
              reservation={reservation}
              displayValidationError={displayValidationError}
              entrancesExits={entrancesExits}
              pActions={pActions}
              onFormValidate={onFormValidate}
              dataBindingsMap={dataBindingsMap} />
          </FlexCol>
          <FlexCol md={6} lg={4} className="mt-1">
            <Label isRequired htmlFor="exitDateOption">
              Exit Date:
            </Label>
            {renderExitDateOption()}
          </FlexCol>
          <FlexCol md={6} lg={4} className="mt-1">
            <Label htmlFor="total-days">Total Days:</Label>
            <p id="total-days" className="mt-1">
              {numberOfDays}
            </p>
          </FlexCol>
        </FlexRow>
        {!isMtWhitneyDayUse && (
          <FlexRow>
            <FlexCol xs={12}>
              <Text>Please use Down Arrow Key to see all available options.</Text>
              <CampsiteSelectionList
                  inputRefs={[
                    addFieldRef?.(componentName, 3),
                    addFieldRef?.(componentName, 4)
                  ]}
                  campsiteLabel={campsiteLabel}
                  validationHighlight={validationHighlight}
                  validationHighlightInput={validationHighlightInput}
                  selectedEntryDate={startDate}
                  selectedExitDate={endDate}
                  divisions={permit.divisions}
                  divisionCampsites={divisionCampsites}
                  stayNightCampsiteSelected={stayNightCampsiteSelected}
                  permitCampsites={permitCampsites}
                  selectedCampsites={selectedCampsites}
                  renderSuggestion={props.renderSuggestion}
                  onFormValidate={onFormValidate}
              />
              {!validationHighlightInput && (
                  <RequiredValidation
                    value={isCampsiteSelected}
                    displayInSummary
                    onValidated={(isInValid, error) =>
                      onFormValidate(isInValid, error, IS_CAMPSITE_PREFERENCES_EMPTY)
                    }
                    controlLabel={campsiteLabel}
                  />
              )}
            </FlexCol>
          </FlexRow>
        )}
      </Stack>
    );
  }

  TripItinerary.defaultProps = defaultProps;
  TripItinerary.attributes = attributes;
  TripItinerary.componentName = 'TripItinerary';
  return TripItinerary;
};
